// src/pages/StudentDashboard.tsx
import React from "react";
import { auth, db, fs } from "../firebase";
import { ref, get, child, onValue, update } from "firebase/database";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { CATALOG_PROCEDURES_FIXED } from "../catalog.fixed"; // keep names clean

// === Firestore (for deleting Scheduler bookings) ===
import {
  collection,
  getDocs,
  writeBatch,
  doc as fsDoc,
  DocumentData,
} from "firebase/firestore";

// === Auth helper for account deletion ===
import { deleteUser } from "firebase/auth";

type PersonalData = {
  userName?: string;
  userEmail?: string;
};

type TrendLabel = "Improving" | "Steady" | "Declining" | "Not enough data";

type ProcedureRow = {
  name: string; // raw DB key (kept for rank paths etc.)
  peakPercentage?: number;
  noOfAttempts?: number;
  attemptsCount: number;
  last10Percentage: number[];
  dataPoints: Array<{ i: number; v: number }>;
  trend: {
    label: TrendLabel;
    last3?: number;
    prev3?: number;
    delta?: number;
  };
  rank?: number | null;
};

type Section = "clinical" | "osce" | "quiz";

/* ---------- Tiny inline SVG icon set (no dependency) ---------- */
type IconProps = {
  size?: number;
  strokeWidth?: number;
  style?: React.CSSProperties;
};
const Svg: React.FC<
  IconProps & { children: React.ReactNode; viewBox?: string }
> = ({
  size = 16,
  strokeWidth = 1.8,
  style,
  viewBox = "0 0 24 24",
  children,
}) => (
  <svg
    width={size}
    height={size}
    viewBox={viewBox}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ display: "inline-block", verticalAlign: "-2px", ...style }}
    aria-hidden="true"
  >
    {children}
  </svg>
);
const IconBell = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 8a6 6 0 0 1 12 0v4c0 1.1.9 2 2 2H4c1.1 0 2-.9 2-2V8Z" />
    <path d="M10 18a2 2 0 0 0 4 0" />
  </Svg>
);
const IconDownload = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3v12" />
    <path d="M7 10l5 5 5-5" />
    <path d="M5 21h14" />
  </Svg>
);
const IconFile = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 2H8a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
  </Svg>
);
const IconUsers = (p: IconProps) => (
  <Svg {...p}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="3" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Svg>
);
/* === icons used === */
const IconCalendar = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </Svg>
);
const IconSun = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </Svg>
);
const IconMoon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" />
  </Svg>
);
const IconLogout = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5" />
    <path d="M21 12H9" />
  </Svg>
);
const IconSearch = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </Svg>
);
const IconTrash = (p: IconProps) => (
  <Svg {...p}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </Svg>
);
/* -------------------------------------------------------------- */

/* ---------- Pretty name helpers ---------- */
const ACRONYMS = new Set(["ABG", "COPD", "DLCO", "IV"]);
const legacyToDisplay = (() => {
  const m: Record<string, string> = {};
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  CATALOG_PROCEDURES_FIXED.forEach((p) => {
    m[norm(p.displayName)] = p.displayName;
    (p.legacyAliases || []).forEach(
      (alias) => (m[norm(alias)] = p.displayName)
    );
  });
  return { map: m, norm };
})();

function titleizeWord(w: string) {
  if (ACRONYMS.has(w.toUpperCase())) return w.toUpperCase();
  return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
}
function humanize(raw: string) {
  if (!raw) return raw;
  let s = raw.replace(/[_-]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2");
  s = s.replace(/\s+/g, " ").trim();
  return s
    .split(" ")
    .map((w) => titleizeWord(w))
    .join(" ");
}
function prettyProcName(raw: string) {
  const key = legacyToDisplay.norm(raw);
  return legacyToDisplay.map[key] || humanize(raw);
}
/* ---------------------------------------- */

/** Theme utils (local) */
function getTheme(): "light" | "dark" {
  return (localStorage.getItem("theme") as "light" | "dark") || "dark";
}
function setTheme(t: "light" | "dark") {
  localStorage.setItem("theme", t);
  if (typeof document !== "undefined") {
    document.body.classList.toggle("light-theme", t === "light");
  }
}

export default function StudentDashboard() {
  const navigate = useNavigate();
  const loc = useLocation() as any;
  const college: string =
    loc?.state?.college ||
    (sessionStorage.getItem("college") ?? "UNKNOWN_COLLEGE");

  const [personal, setPersonal] = React.useState<PersonalData | null>(null);
  const [procedures, setProcedures] = React.useState<ProcedureRow[]>([]);
  const [osce, setOsce] = React.useState<{
    semester: string;
    dateTime?: string;
    stations?: Record<string, boolean>;
  } | null>(null);
  const [quizzes, setQuizzes] = React.useState<
    Array<{
      subject: string;
      averageScore?: number;
      quizes?: Record<string, number>;
      sectionName?: string;
    }>
  >([]);
  const [section, setSection] = React.useState<Section>("clinical");

  const [notifList, setNotifList] = React.useState<
    Array<{ id: string; text?: string; at?: string; read?: boolean }>
  >([]);
  const [showNotif, setShowNotif] = React.useState(false);
  const unreadCount = notifList.filter((n) => !n.read).length;

  const [theme, setThemeState] = React.useState<"light" | "dark">(getTheme());

  // Search state for clinical procedures
  const [query, setQuery] = React.useState("");

  // === Delete account modal state ===
  const [showDelete, setShowDelete] = React.useState(false);
  const [deleteTyping, setDeleteTyping] = React.useState("");
  const [deleteBusy, setDeleteBusy] = React.useState(false);
  const [deleteErr, setDeleteErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (college) sessionStorage.setItem("college", college);
    (async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/login");
        return;
      }
      const uid = user.uid;
      const root = ref(db);

      // Personal
      const pdSnap = await get(
        child(root, `Colleges/${college}/Users/${uid}/Personal Data`)
      );
      setPersonal(pdSnap.exists() ? (pdSnap.val() as PersonalData) : {});

      // Game Modes → rows + trend (shape unchanged)
      const gmSnap = await get(
        child(root, `Colleges/${college}/Users/${uid}/Game Modes`)
      );
      if (gmSnap.exists()) {
        const items: any = gmSnap.val();
        const arr: ProcedureRow[] = Object.keys(items).map((k) => {
          const raw = items[k]?.last10Percentage ?? [];
          const last10 = normalizeNumArray(raw);
          const attemptsCount = items[k]?.noOfAttempts ?? last10.length;
          const dataPoints = last10.map((v: number, idx: number) => ({
            i: idx + 1,
            v,
          }));
          return {
            name: k,
            peakPercentage: items[k]?.peakPercentage ?? undefined,
            noOfAttempts: items[k]?.noOfAttempts ?? undefined,
            attemptsCount,
            last10Percentage: last10,
            dataPoints,
            trend: computeTrend(last10),
            rank: null,
          };
        });
        setProcedures(arr);
      } else {
        setProcedures([]);
      }

      // OSCE latest
      const osceSnap = await get(
        child(root, `Colleges/${college}/Users/${uid}/OSCE Data`)
      );
      if (osceSnap.exists()) {
        const obj: Record<string, any> = osceSnap.val();
        const latestSemKey = pickLatestSemesterKey(obj);
        if (latestSemKey) {
          setOsce({
            semester: latestSemKey,
            dateTime: obj[latestSemKey]?.dateTime,
            stations: obj[latestSemKey]?.stations || {},
          });
        } else setOsce(null);
      } else setOsce(null);

      // Quizzes
      const qSnap = await get(
        child(root, `Colleges/${college}/Users/${uid}/Quizes Data`)
      );
      if (qSnap.exists()) {
        const q: Record<string, any> = qSnap.val();
        const list = Object.keys(q).map((subject) => ({
          subject,
          averageScore: q[subject]?.averageScore,
          quizes: q[subject]?.quizes || {},
          sectionName: q[subject]?.sectionName,
        }));
        setQuizzes(list);
      } else setQuizzes([]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live Notifications listener
  React.useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const uid = user.uid;
    const notifRef = ref(db, `Colleges/${college}/Notifications/${uid}`);
    const unsub = onValue(notifRef, (snap) => {
      const v = snap.val() as Record<string, any> | null;
      if (!v) return setNotifList([]);
      const list = Object.entries(v)
        .map(([id, n]) => ({ id, text: n?.text, at: n?.at, read: !!n?.read }))
        .sort((a, b) => String(b.at || "").localeCompare(String(a.at || "")));
      setNotifList(list);
    });
    return () => unsub();
  }, [college]);

  function logout() {
    auth.signOut();
    sessionStorage.removeItem("college");
    navigate("/login");
  }

  // Mark all notifications read
  async function markAllRead() {
    if (!notifList.length || !auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const updates: Record<string, any> = {};
    notifList.forEach((n) => {
      updates[`Colleges/${college}/Notifications/${uid}/${n.id}/read`] = true;
    });
    await update(ref(db), updates);
  }

  // Theme toggle action
  function toggleTheme() {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    setThemeState(next);
  }

  // Export CSV (Time Spent removed)
  function exportMyCSV() {
    const uid = auth.currentUser?.uid || "me";
    const rows: string[] = [];
    rows.push("section,key,value");
    rows.push(`personal,name,${personal?.userName ?? ""}`);
    rows.push(`personal,email,${personal?.userEmail ?? ""}`);
    procedures.forEach((p) => {
      rows.push(`procedure,${p.name}_peakPercentage,${p.peakPercentage ?? ""}`);
      rows.push(`procedure,${p.name}_attempts,${p.attemptsCount}`);
      rows.push(`procedure,${p.name}_last10,${p.last10Percentage.join("|")}`);
    });
    if (osce) {
      rows.push(`osce,semester,${osce.semester}`);
      rows.push(`osce,dateTime,${osce.dateTime ?? ""}`);
      if (osce.stations) {
        Object.entries(osce.stations).forEach(([k, v]) => {
          rows.push(`osce,station_${k},${v ? "pass" : "fail"}`);
        });
      }
    }
    quizzes.forEach((q) => {
      rows.push(
        `quiz,${q.sectionName || q.subject}_avg,${q.averageScore ?? ""}`
      );
      if (q.quizes) {
        Object.entries(q.quizes).forEach(([k, v]) => {
          rows.push(`quiz,${q.sectionName || q.subject}_${k},${v}`);
        });
      }
    });
    const blob = new Blob([rows.join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `viscer_${uid}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // ---------- helpers (unchanged) ----------
  function normalizeNumArray(x: any): number[] {
    if (Array.isArray(x))
      return x.map((n) => Number(n)).filter((n) => !Number.isNaN(n));
    if (x && typeof x === "object") {
      return Object.keys(x)
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => Number(x[k]))
        .filter((n) => !Number.isNaN(n));
    }
    return [];
  }
  function round1(n: number) {
    return Math.round(n * 10) / 10;
  }
  function avg(nums: number[]): number {
    if (!nums.length) return 0;
    return round1(nums.reduce((a, b) => a + b, 0) / nums.length);
  }
  function computeTrend(last10: number[]): ProcedureRow["trend"] {
    if (!last10 || last10.length < 6) return { label: "Not enough data" };
    const last3 = avg(last10.slice(-3));
    const prev3 = avg(last10.slice(-6, -3));
    const delta = round1(last3 - prev3);
    if (delta >= 5) return { label: "Improving", last3, prev3, delta };
    if (delta <= -5) return { label: "Declining", last3, prev3, delta };
    return { label: "Steady", last3, prev3, delta };
  }
  async function checkRank(proc: string, myScore?: number) {
    if (myScore == null) return;
    const root = ref(db);
    const lbSnap = await get(
      child(root, `Colleges/${college}/Leaderboards/${proc}`)
    );
    if (!lbSnap.exists()) {
      setProcedures((old) =>
        old.map((p) => (p.name === proc ? { ...p, rank: null } : p))
      );
      return;
    }
    const entries: Record<string, number> = lbSnap.val();
    const scores = Object.values(entries) as number[];
    const sortedDesc = [...scores].sort((a, b) => b - a);
    const betterCount = sortedDesc.filter((s) => s > myScore).length;
    const rank = betterCount + 1;
    setProcedures((old) =>
      old.map((p) => (p.name === proc ? { ...p, rank } : p))
    );
  }
  function pillClass(label: TrendLabel) {
    if (label === "Improving") return "pill good";
    if (label === "Declining") return "pill bad";
    if (label === "Steady") return "pill warn";
    return "pill muted";
  }
  function parseMaybeISO(s?: string): number {
    if (!s) return 0;
    const d = Date.parse(s);
    if (!Number.isNaN(d)) return d;
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
    if (!m) return 0;
    const [, yy, mm, dd, hh, mi, ss] = m.map(Number) as any;
    return new Date(yy, mm - 1, dd, hh, mi, ss).getTime();
  }
  function pickLatestSemesterKey(osceObj: Record<string, any>): string | null {
    const keys = Object.keys(osceObj || {});
    if (!keys.length) return null;
    const withTimes = keys.map((k) => ({
      k,
      t: parseMaybeISO(osceObj[k]?.dateTime),
    }));
    withTimes.sort((a, b) => b.t - a.t);
    if (withTimes[0].t > 0) return withTimes[0].k;
    const withN = keys.map((k) => ({
      k,
      n: Number(String(k).replace(/\D+/g, "")) || 0,
    }));
    withN.sort((a, b) => b.n - a.n);
    return withN[0].k;
  }

  // ---- Derived (unchanged) ----
  const { top3, bottom3 } = React.useMemo(() => {
    const rows = procedures
      .map((p) => {
        const last5 = p.last10Percentage.slice(-5);
        const avg5 =
          last5.length > 0
            ? Math.round(
                (last5.reduce((a, b) => a + b, 0) / last5.length) * 10
              ) / 10
            : null;
        return {
          name: p.name,
          avg: avg5,
          attemptsCount: p.attemptsCount,
        };
      })
      .filter((r) => r.avg !== null && r.attemptsCount >= 3) as Array<{
      name: string;
      avg: number;
      attemptsCount: number;
    }>;

    const sorted = [...rows].sort((a, b) => b.avg - a.avg);
    return {
      top3: sorted.slice(0, 3),
      bottom3: sorted.slice(-3).reverse(),
    };
  }, [procedures]);

  // Filtered procedures by search query (matches pretty display name)
  const filteredProcedures = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return procedures;
    return procedures.filter((p) =>
      prettyProcName(p.name).toLowerCase().includes(q)
    );
  }, [procedures, query]);

  // === Delete my account handlers ===
  async function handleDeleteMyAccount() {
    setShowDelete(true);
    setDeleteTyping("");
    setDeleteErr(null);
  }

  async function performDeletion() {
    if (deleteBusy) return;
    setDeleteBusy(true);
    setDeleteErr(null);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in");
      const uid = user.uid;

      // 1) Firestore: delete my Scheduler bookings across all weeks
      const weeksCol = collection(fs, "scheduler", college, "weeks");
      const weeksSnap = await getDocs(weeksCol);
      for (const weekDoc of weeksSnap.docs) {
        const bookingsCol = collection(
          fs,
          "scheduler",
          college,
          "weeks",
          weekDoc.id,
          "bookings"
        );
        const bookSnap = await getDocs(bookingsCol);
        if (!bookSnap.empty) {
          const batch = writeBatch(fs);
          bookSnap.forEach((d: DocumentData) => {
            const data = d.data();
            if (data?.userId === uid) {
              batch.delete(
                fsDoc(
                  fs,
                  "scheduler",
                  college,
                  "weeks",
                  weekDoc.id,
                  "bookings",
                  d.id
                )
              );
            }
          });
          await batch.commit();
        }
      }

      // 2) Realtime DB: remove profile + mappings
      const updates: Record<string, any> = {};
      updates[`UserToCollege/${uid}`] = null;
      updates[`UserRoles/${uid}`] = null;
      updates[`Colleges/${college}/Users/${uid}`] = null;
      updates[`Colleges/${college}/Notifications/${uid}`] = null;
      updates[`Users/${uid}`] = null; // in case you store global profile node
      await update(ref(db), updates);

      // 3) Try to delete the Auth account
      try {
        await deleteUser(user);
      } catch (e: any) {
        // If recent login is required, we still consider data deletion successful
        if (String(e?.code || "").includes("requires-recent-login")) {
          setDeleteErr(
            "Your data was deleted. Please sign in again to complete account removal if needed."
          );
        } else {
          setDeleteErr("Your data was deleted. Sign out completed.");
        }
      }

      // 4) Sign out and go to Login
      await auth.signOut();
      sessionStorage.removeItem("college");
      navigate("/login", { replace: true });
    } catch (e: any) {
      setDeleteErr(
        e?.message || "We couldn't complete deletion. Please try again."
      );
      setDeleteBusy(false);
    }
  }

  // ---------- UI ----------
  return (
    <div className="container">
      {/* Header with logo, title, icons */}
      <div className="row" style={{ marginBottom: 16, alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img
            src="/viscer-logo.svg"
            alt="VisceR"
            height={50}
            style={{ display: "block" }}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.src = "/viscer-logo.png";
            }}
          />
          <h2 style={{ margin: 0 }}>Student Dashboard</h2>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            className="btn"
            onClick={() => setShowNotif((s) => !s)}
            aria-label="Notifications"
          >
            <IconBell />{" "}
            {unreadCount > 0 && (
              <span className="badge" style={{ marginLeft: 6 }}>
                {unreadCount}
              </span>
            )}
          </button>
          <button className="btn" onClick={exportMyCSV}>
            <IconDownload /> <span style={{ marginLeft: 6 }}>Export CSV</span>
          </button>
          <button className="btn" onClick={() => window.print()}>
            <IconFile /> <span style={{ marginLeft: 6 }}>Export PDF</span>
          </button>
          <button
            className="btn"
            onClick={() => navigate("/s/cohorts", { state: { college } })}
          >
            <IconUsers /> <span style={{ marginLeft: 6 }}>My Cohorts</span>
          </button>
          {/* === Scheduler button (unchanged) === */}
          <button
            className="btn"
            onClick={() => navigate("/s/scheduler", { state: { college } })}
            title="Open Scheduler"
          >
            <IconCalendar /> <span style={{ marginLeft: 6 }}>Scheduler</span>
          </button>

          {/* (MOVED) Delete button removed from header */}

          <button
            className="btn"
            onClick={toggleTheme}
            aria-label="Toggle theme"
          >
            {theme === "dark" ? (
              <>
                <IconSun /> <span style={{ marginLeft: 6 }}>Light</span>
              </>
            ) : (
              <>
                <IconMoon /> <span style={{ marginLeft: 6 }}>Dark</span>
              </>
            )}
          </button>
          <button className="btn" onClick={logout}>
            <IconLogout /> <span style={{ marginLeft: 6 }}>Log out</span>
          </button>
        </div>
      </div>

      {/* Tabs control + (NEW) Delete button on the right */}
      <div
        className="card"
        style={{
          marginBottom: 16,
          padding: 8,
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <span className="small" style={{ opacity: 0.8 }}>
          View:
        </span>
        <button
          className="btn"
          aria-pressed={section === "clinical"}
          onClick={() => setSection("clinical")}
          style={{
            background:
              section === "clinical" ? "rgba(59,130,246,.12)" : "transparent",
            borderColor:
              section === "clinical" ? "rgba(59,130,246,.5)" : "var(--line)",
          }}
        >
          Clinical Procedures
        </button>
        <button
          className="btn"
          aria-pressed={section === "osce"}
          onClick={() => setSection("osce")}
          style={{
            background:
              section === "osce" ? "rgba(59,130,246,.12)" : "transparent",
            borderColor:
              section === "osce" ? "rgba(59,130,246,.5)" : "var(--line)",
          }}
        >
          OSCE
        </button>
        <button
          className="btn"
          aria-pressed={section === "quiz"}
          onClick={() => setSection("quiz")}
          style={{
            background:
              section === "quiz" ? "rgba(59,130,246,.12)" : "transparent",
            borderColor:
              section === "quiz" ? "rgba(59,130,246,.5)" : "var(--line)",
          }}
        >
          Quiz
        </button>

        {/* NEW: Delete button here on the right */}
        <button
          className="btn"
          onClick={handleDeleteMyAccount}
          title="Delete my account and data"
          style={{
            borderColor: "rgba(239,68,68,.5)",
            background: "rgba(239,68,68,.08)",
            marginLeft: "auto",
          }}
        >
          <IconTrash /> <span style={{ marginLeft: 6 }}>Delete My Account</span>
        </button>
      </div>

      {/* Delete confirmation modal */}
      {showDelete && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.5)",
            display: "grid",
            placeItems: "center",
            zIndex: 9999,
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="card"
            style={{
              maxWidth: 520,
              borderColor: "rgba(239,68,68,.5)",
              background: "var(--surface-1)",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Delete My Account</h3>
            <p className="small" style={{ marginTop: 6 }}>
              This will permanently delete your VisceR account and personal
              data: your profile, college mappings, notifications, and your
              headset booking history. This action cannot be undone.
            </p>
            <p className="small" style={{ marginTop: 6 }}>
              To confirm, type <b>DELETE</b> below and press{" "}
              <b>Confirm &amp; Delete</b>.
            </p>
            <input
              className="input"
              placeholder="Type DELETE to confirm"
              value={deleteTyping}
              onChange={(e) => setDeleteTyping(e.target.value)}
              style={{ marginTop: 8 }}
            />
            {deleteErr && (
              <p className="small" style={{ color: "#ff6b6b", marginTop: 8 }}>
                {deleteErr}
              </p>
            )}
            <div className="row" style={{ marginTop: 12, gap: 8 }}>
              <button
                className="btn"
                onClick={() => setShowDelete(false)}
                disabled={deleteBusy}
              >
                Cancel
              </button>
              <button
                className="btn"
                onClick={performDeletion}
                disabled={deleteTyping !== "DELETE" || deleteBusy}
                style={{
                  borderColor: "rgba(239,68,68,.6)",
                  background: "rgba(239,68,68,.12)",
                }}
              >
                {deleteBusy ? "Deleting…" : "Confirm & Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile */}
      <div className="grid" style={{ gap: 16 }}>
        <div className="card">
          <h3>Profile</h3>
          <p className="kv">
            <span>Name</span> <b>{personal?.userName || "—"}</b>
          </p>
          <p className="kv">
            <span>Email</span> <b>{personal?.userEmail || "—"}</b>
          </p>
          {/* Time Spent removed */}
        </div>
      </div>

      {/* Notifications panel */}
      {showNotif && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="row">
            <h3 style={{ margin: 0 }}>Notifications</h3>
            <button className="btn" onClick={markAllRead}>
              Mark all read
            </button>
          </div>
          {notifList.length === 0 ? (
            <p className="small">No notifications.</p>
          ) : (
            <ul style={{ margin: 8, paddingLeft: 16 }}>
              {notifList.map((n) => (
                <li key={n.id} className="small">
                  {n.at ? `${n.at} — ` : ""} {n.text || "(note)"}{" "}
                  {!n.read && <b>(new)</b>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* SECTION: Clinical */}
      {section === "clinical" && (
        <>
          {/* Top 3 + Needs Improvement — stacked */}
          <div style={{ display: "grid", gap: 16, marginTop: 16 }}>
            <div className="card">
              <h3>Top 3 Procedures</h3>
              {(() => {
                const rows = procedures
                  .map((p) => {
                    const last5 = p.last10Percentage.slice(-5);
                    const avg5 =
                      last5.length > 0
                        ? Math.round(
                            (last5.reduce((a, b) => a + b, 0) / last5.length) *
                              10
                          ) / 10
                        : null;
                    return {
                      name: p.name,
                      avg: avg5,
                      attemptsCount: p.attemptsCount,
                    };
                  })
                  .filter(
                    (r) => r.avg !== null && r.attemptsCount >= 3
                  ) as Array<{ name: string; avg: number }>;
                const top3 = [...rows]
                  .sort((a, b) => b.avg - a.avg)
                  .slice(0, 3);
                return top3.length === 0 ? (
                  <p className="small">Not enough data yet.</p>
                ) : (
                  <ul style={{ marginLeft: 16 }}>
                    {top3.map((r) => (
                      <li key={r.name} className="small">
                        <b>{prettyProcName(r.name)}</b> — {r.avg}%
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>

            <div className="card">
              <h3>Needs Improvement</h3>
              {(() => {
                const rows = procedures
                  .map((p) => {
                    const last5 = p.last10Percentage.slice(-5);
                    const avg5 =
                      last5.length > 0
                        ? Math.round(
                            (last5.reduce((a, b) => a + b, 0) / last5.length) *
                              10
                          ) / 10
                        : null;
                    return {
                      name: p.name,
                      avg: avg5,
                      attemptsCount: p.attemptsCount,
                    };
                  })
                  .filter(
                    (r) => r.avg !== null && r.attemptsCount >= 3
                  ) as Array<{ name: string; avg: number }>;
                const bottom3 = [...rows]
                  .sort((a, b) => a.avg - b.avg)
                  .slice(0, 3);
                return bottom3.length === 0 ? (
                  <p className="small">Not enough data yet.</p>
                ) : (
                  <ul style={{ marginLeft: 16 }}>
                    {bottom3.map((r) => (
                      <li key={r.name} className="small">
                        <b>{prettyProcName(r.name)}</b> — {r.avg}%
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
          </div>

          {/* Procedures list + SEARCH */}
          <div className="card" style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: "center", gap: 12 }}>
              <h3 style={{ margin: 0 }}>Your Procedures</h3>
              {/* Search input on the right */}
              <div style={{ marginLeft: "auto", position: "relative" }}>
                <IconSearch
                  size={16}
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    opacity: 0.7,
                  }}
                />
                <input
                  className="input"
                  placeholder="Search procedures…"
                  aria-label="Search procedures"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  style={{ paddingLeft: 32, width: 260 }}
                />
              </div>
            </div>

            {(query ? filteredProcedures : procedures).length === 0 ? (
              <p className="small" style={{ marginTop: 12 }}>
                {query
                  ? "No procedures match your search."
                  : "No procedures yet."}
              </p>
            ) : (
              <div style={{ display: "grid", gap: 16, marginTop: 12 }}>
                {(query ? filteredProcedures : procedures).map((p) => (
                  <div
                    key={p.name}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(260px, 360px) 1fr",
                      gap: 16,
                      alignItems: "center",
                      border: "1px solid var(--line)",
                      borderRadius: 12,
                      padding: 12,
                      background: "var(--surface-1)",
                    }}
                  >
                    {/* Left info */}
                    <div>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 16,
                          marginBottom: 4,
                        }}
                      >
                        {prettyProcName(p.name)}
                      </div>
                      <div className="small" style={{ marginBottom: 6 }}>
                        peak <b>{p.peakPercentage ?? "—"}%</b> • attempts{" "}
                        <b>{p.attemptsCount}</b>
                      </div>

                      {/* Performance overview */}
                      <div style={{ marginTop: 6 }}>
                        <div
                          className="small"
                          style={{ opacity: 0.8, marginBottom: 4 }}
                        >
                          Performance overview
                        </div>
                        <span
                          className={pillClass(p.trend.label)}
                          style={{ marginRight: 8 }}
                        >
                          {p.trend.label}
                        </span>
                        {p.trend.last3 !== undefined &&
                          p.trend.prev3 !== undefined && (
                            <span className="small">
                              {p.trend.delta && p.trend.delta > 0
                                ? "Up"
                                : p.trend.delta && p.trend.delta < 0
                                ? "Down"
                                : "No change"}{" "}
                              <b>{Math.abs(p.trend.delta || 0)} pts</b> (last 3
                              avg <b>{p.trend.last3}%</b> vs previous 3 avg{" "}
                              <b>{p.trend.prev3}%</b>)
                            </span>
                          )}
                        {p.trend.label === "Not enough data" && (
                          <span className="small">
                            {" "}
                            Need at least 6 attempts to assess trend.
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Chart (exact original styling) */}
                    <div
                      style={{
                        width: "100%",
                        height: 140,
                        background: "var(--surface-2)",
                        border: "1px solid var(--line)",
                        borderRadius: 10,
                        padding: "8px 10px",
                      }}
                    >
                      {p.dataPoints.length ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={p.dataPoints}>
                            <CartesianGrid
                              stroke="rgba(255,255,255,0.08)"
                              strokeDasharray="3 3"
                            />
                            <XAxis
                              dataKey="i"
                              tick={{
                                fontSize: 11,
                                fill: "rgba(255,255,255,.7)",
                              }}
                              label={{
                                value: "Attempt",
                                position: "insideBottomRight",
                                offset: -4,
                                fill: "rgba(255,255,255,.8)",
                                fontSize: 11,
                              }}
                              allowDecimals={false}
                            />
                            <YAxis
                              domain={[0, 100]}
                              tick={{
                                fontSize: 11,
                                fill: "rgba(255,255,255,.7)",
                              }}
                              label={{
                                value: "Score (%)",
                                angle: -90,
                                position: "insideLeft",
                                fill: "rgba(255,255,255,.8)",
                                fontSize: 11,
                              }}
                              allowDecimals={false}
                            />
                            <Tooltip
                              formatter={(value: any) => [`${value}%`, "Score"]}
                              labelFormatter={(label: any) =>
                                `Attempt ${label}`
                              }
                              contentStyle={{
                                background: "#0b0f15",
                                border: "1px solid var(--line)",
                              }}
                            />
                            <Line
                              type="monotone"
                              dataKey="v"
                              stroke="#60a5fa"
                              strokeWidth={2}
                              dot={{ r: 2 }}
                              isAnimationActive={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <div
                          className="small"
                          style={{ padding: 8, color: "var(--muted)" }}
                        >
                          No attempts yet
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* SECTION: OSCE */}
      {section === "osce" && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>OSCE (latest)</h3>
          {!osce ? (
            <p className="small">—</p>
          ) : (
            <>
              <p className="kv">
                <span>Semester</span> <b>{osce.semester}</b> &nbsp;
                <span className="small">at {osce.dateTime || "—"}</span>
              </p>
              {osce.stations && Object.keys(osce.stations).length ? (
                <table className="table" style={{ marginTop: 8 }}>
                  <thead>
                    <tr>
                      <th>Station</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(osce.stations).map((k) => (
                      <tr key={k}>
                        <td>{k}</td>
                        <td>{osce.stations![k] ? "✓ Passed" : "✗ Failed"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="small">No station data.</p>
              )}
            </>
          )}
        </div>
      )}

      {/* SECTION: Quiz */}
      {section === "quiz" && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Quizzes</h3>
          {quizzes.length === 0 ? (
            <p className="small">—</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Avg</th>
                  <th>Quizzes</th>
                </tr>
              </thead>
              <tbody>
                {quizzes.map((q) => (
                  <tr key={q.subject}>
                    <td>{q.sectionName || q.subject}</td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          borderColor:
                            (q.averageScore ?? 0) < 0
                              ? "rgba(239,68,68,.5)"
                              : "rgba(59,130,246,.4)",
                          background:
                            (q.averageScore ?? 0) < 0
                              ? "rgba(239,68,68,.08)"
                              : "rgba(59,130,246,.08)",
                        }}
                      >
                        {q.averageScore ?? "—"}
                      </span>
                    </td>
                    <td className="small">
                      {q.quizes && Object.keys(q.quizes).length
                        ? Object.entries(q.quizes)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join("  •  ")
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
